import { useState, useEffect } from 'react'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import type { ContentItem } from '../../api/contentApi'

/* eslint-disable @typescript-eslint/no-explicit-any */
type Payload = Record<string, any>

interface GalleryWallSetEditorProps {
  item: ContentItem
  onSave: (payload: Payload) => void
  saving?: boolean
}

/**
 * Editor for a `gallery_wall_set` — a room's curated list of 2-3 gallery-wall sets, each a
 * bundle of ~3 prints. The curator tunes each set's name / title / narrative / arrangement /
 * price; the member pieces and imagery are computed by the curation engine and shown
 * read-only. Approving publishes the whole list to arthaus.featured_gallery_sets.
 */
export function GalleryWallSetEditor({ item, onSave, saving }: GalleryWallSetEditorProps) {
  const [p, setP] = useState<Payload>((item.payload as Payload) || {})
  useEffect(() => {
    setP((item.payload as Payload) || {})
  }, [item.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const sets: Payload[] = Array.isArray(p.sets) ? p.sets : []

  const setField = (i: number, k: string, v: any) =>
    setP((prev) => {
      const next = Array.isArray(prev.sets) ? [...prev.sets] : []
      next[i] = { ...next[i], [k]: v }
      return { ...prev, sets: next }
    })

  if (!sets.length) {
    return <p className="text-sm text-muted-foreground">No sets in this item yet.</p>
  }

  return (
    <div className="space-y-8">
      {sets.map((s, i) => {
        const pieces: Payload[] = Array.isArray(s.pieces) ? s.pieces : []
        return (
          <div key={i} className="space-y-4 border rounded-md p-4">
            {s.image_url ? (
              <img src={String(s.image_url)} alt="" className="w-full h-48 object-cover rounded-md border" />
            ) : null}

            <div className="grid grid-cols-2 gap-4">
              <Field label="Set name">
                <Input
                  value={String(s.name || s.title || '')}
                  onChange={(e) => {
                    setField(i, 'name', e.target.value)
                    setField(i, 'title', e.target.value)
                  }}
                />
              </Field>
              <Field label="Arrangement">
                <Input value={String(s.arrangement || '')} onChange={(e) => setField(i, 'arrangement', e.target.value)} />
              </Field>
            </div>

            <Field label="Title (display)">
              <Input value={String(s.title || '')} onChange={(e) => setField(i, 'title', e.target.value)} />
            </Field>

            <Field label="Rationale (the shared thread)">
              <Textarea rows={2} value={String(s.rationale || '')} onChange={(e) => setField(i, 'rationale', e.target.value)} />
            </Field>
            <Field label="Narrative (editorial paragraph)">
              <Textarea rows={3} value={String(s.narrative || '')} onChange={(e) => setField(i, 'narrative', e.target.value)} />
            </Field>

            <div className="grid grid-cols-3 gap-4">
              <Field label="Composition"><Input value={String(s.composition || '')} onChange={(e) => setField(i, 'composition', e.target.value)} /></Field>
              <Field label="Price (display)"><Input value={String(s.price || '')} onChange={(e) => setField(i, 'price', e.target.value)} /></Field>
              <Field label="Per piece"><Input value={String(s.per_piece || '')} onChange={(e) => setField(i, 'per_piece', e.target.value)} /></Field>
            </div>

            <Field label={`Pieces (${pieces.length}, read-only)`}>
              <div className="space-y-1.5">
                {pieces.map((pc, j) => (
                  <div key={j} className="flex items-center justify-between text-sm border rounded-md px-3 py-1.5">
                    <span>{pc.title}{pc.artist ? ` · ${pc.artist}` : ''}</span>
                    <span className="text-muted-foreground">{pc.price ? `$${pc.price}` : ''}</span>
                  </div>
                ))}
              </div>
            </Field>
          </div>
        )
      })}

      <div className="flex justify-end">
        <Button onClick={() => onSave(p)} disabled={saving}>
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </div>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
    </div>
  )
}
